import { useQuery } from '@apollo/client';
import { X, Loader, FileText } from 'lucide-react';
import { ESTADO_CUENTA_SOLDADO } from '../../graphql/cuentasCobro';

const MESES_CORTO = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

function formatMoneda(v) {
  if (v == null) return '$0';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

function formatFecha(f) {
  if (!f) return '-';
  return new Date(f).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function EstadoCuentaSoldado({ ocupanteId, onClose }) {
  const { data, loading } = useQuery(ESTADO_CUENTA_SOLDADO, {
    variables: { ocupante_hospedaje_id: parseInt(ocupanteId) },
    fetchPolicy: 'network-only',
  });

  const estado = data?.estadoCuentaSoldado;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-cc modal-cc--lg" onClick={e => e.stopPropagation()}>
        <div className="modal-cc__header">
          <h2>Estado de Cuenta</h2>
          <button className="btn-cerrar" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-cc__body">
          {loading || !estado ? (
            <div className="loading-overlay">
              <Loader size={32} className="spinner" />
            </div>
          ) : (
            <>
              {/* Info ocupante */}
              <div className="ec-ocupante-info">
                <div className="ec-ocupante-nombre">
                  <strong>{estado.ocupante.nombre}</strong>
                  <span>{estado.ocupante.grado_militar_nombre || 'Sin grado'}</span>
                </div>
                <div className="ec-ocupante-datos">
                  <span>Doc: {estado.ocupante.numero_documento}</span>
                  <span>Hab {estado.ocupante.habitacion_numero} / Cama {estado.ocupante.cama_numero || '-'}</span>
                  <span>Tarifa: {formatMoneda(estado.ocupante.precio_noche)}/noche</span>
                </div>
              </div>

              {/* Resumen totales */}
              <div className="ec-totales">
                <div className="ec-total-item">
                  <span>Total Facturado</span>
                  <strong>{formatMoneda(estado.total_facturado)}</strong>
                </div>
                <div className="ec-total-item ec-total--verde">
                  <span>Total Pagado</span>
                  <strong>{formatMoneda(estado.total_pagado)}</strong>
                </div>
                <div className="ec-total-item ec-total--rojo">
                  <span>Saldo Total</span>
                  <strong>{formatMoneda(estado.saldo_total)}</strong>
                </div>
              </div>

              {/* Tabla tipo ledger */}
              {estado.cuentas.length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <FileText size={32} color="#9ca3af" />
                  <p className="empty-state__title">Sin cuentas de cobro</p>
                  <p className="empty-state__subtitle">Aún no se han generado cuentas para este soldado</p>
                </div>
              ) : (
                <div className="ec-tabla-wrapper">
                  <table className="ec-tabla">
                    <thead>
                      <tr>
                        <th>Periodo</th>
                        <th>Código</th>
                        <th className="text-right">Cargo</th>
                        <th className="text-right">Abono</th>
                        <th className="text-right">Saldo</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estado.cuentas.map(cc => (
                        <tr key={cc.id}>
                          <td><strong>{MESES_CORTO[cc.periodo_mes - 1]} {cc.periodo_anio}</strong></td>
                          <td className="td-codigo">{cc.codigo}</td>
                          <td className="text-right">{formatMoneda(cc.monto_total)}</td>
                          <td className="text-right" style={{ color: '#22c55e' }}>{formatMoneda(cc.monto_pagado)}</td>
                          <td className="text-right font-bold" style={{ color: cc.saldo_pendiente > 0 ? '#ef4444' : '#22c55e' }}>
                            {formatMoneda(cc.saldo_pendiente)}
                          </td>
                          <td>
                            <span className={`badge badge--${cc.estado}`}>
                              {cc.estado.charAt(0).toUpperCase() + cc.estado.slice(1)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Historial de pagos */}
              {estado.pagos.length > 0 && (
                <div className="ec-seccion">
                  <h3>Historial de Pagos</h3>
                  <div className="ec-tabla-wrapper">
                    <table className="ec-tabla ec-tabla--pagos">
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Código</th>
                          <th>Periodo</th>
                          <th>Método</th>
                          <th className="text-right">Monto</th>
                          <th>Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {estado.pagos.map(p => (
                          <tr key={p.id} className={p.anulado ? 'pago-anulado' : ''}>
                            <td>{formatFecha(p.fecha_pago)}</td>
                            <td className="td-codigo">{p.codigo}</td>
                            <td>{MESES_CORTO[(p.periodo_mes || 1) - 1]} {p.periodo_anio}</td>
                            <td>{p.metodo_pago}</td>
                            <td className="text-right" style={{ color: p.anulado ? '#9ca3af' : '#22c55e', fontWeight: 600 }}>
                              {formatMoneda(p.monto)}
                            </td>
                            <td>{p.anulado ? <span style={{ color: '#ef4444', fontSize: '0.75rem' }}>ANULADO</span> : (p.notas || '-')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-cc__footer">
          <button className="btn-modal" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* Estilos */
const style = document.createElement('style');
style.textContent = `
.ec-ocupante-info { padding: 1rem; background: var(--hotel-background); border-radius: 8px; margin-bottom: 1rem; }
.ec-ocupante-nombre { display: flex; gap: 0.5rem; align-items: baseline; margin-bottom: 0.4rem; }
.ec-ocupante-nombre strong { font-size: 1.1rem; }
.ec-ocupante-nombre span { font-size: 0.85rem; color: var(--hotel-text-secondary); }
.ec-ocupante-datos { display: flex; gap: 1.25rem; font-size: 0.8rem; color: var(--hotel-text-secondary); flex-wrap: wrap; }
.ec-totales { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; margin-bottom: 1.25rem; }
.ec-total-item { display: flex; flex-direction: column; align-items: center; padding: 0.75rem; background: var(--hotel-background); border-radius: 8px; }
.ec-total-item span { font-size: 0.7rem; text-transform: uppercase; color: var(--hotel-text-secondary); }
.ec-total-item strong { font-size: 1.25rem; }
.ec-total--verde strong { color: #22c55e; }
.ec-total--rojo strong { color: #ef4444; }
.ec-seccion { margin-top: 1.25rem; }
.ec-seccion h3 { font-size: 0.85rem; font-weight: 700; color: var(--hotel-text-secondary); text-transform: uppercase; margin: 0 0 0.5rem; }
.ec-tabla-wrapper { overflow-x: auto; margin-bottom: 0.5rem; }
.ec-tabla { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
.ec-tabla th { padding: 0.5rem 0.75rem; text-align: left; font-size: 0.7rem; text-transform: uppercase; color: var(--hotel-text-secondary); border-bottom: 2px solid var(--hotel-border); }
.ec-tabla td { padding: 0.5rem 0.75rem; border-bottom: 1px solid var(--hotel-border); }
.ec-tabla tbody tr:hover { background: var(--hotel-background); }
@media (max-width: 640px) { .ec-totales { grid-template-columns: 1fr; } }
`;
if (!document.getElementById('ec-soldado-styles')) {
  style.id = 'ec-soldado-styles';
  document.head.appendChild(style);
}

export default EstadoCuentaSoldado;
